import React, { memo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Skeleton,
  Box,
  Tooltip,
  TableSortLabel,
  useTheme,
  alpha,
  Typography,
  Stack,
  Chip,
  Checkbox,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as DoctorIcon,
  CalendarMonth as DateIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { extractTimeHm } from '../../utils/shiftTime';
import { ja } from 'date-fns/locale';
import { motion } from 'framer-motion';
import type { DoctorShift, DoctorShiftFilterOptions } from '../../types/doctorShift';

interface DoctorShiftTableProps {
  data: DoctorShift[];
  isLoading: boolean;
  onViewDetails: (shift: DoctorShift) => void;
  onEdit: (shift: DoctorShift) => void;
  onDelete: (shift: DoctorShift) => void;
  filters: DoctorShiftFilterOptions;
  onFiltersChange: (filters: DoctorShiftFilterOptions) => void;
  meta?: {
    current_page: number;
    total_pages: number;
    total_records: number;
    page_size: number;
  };
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (sortBy: keyof DoctorShift) => void;
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  compactMode?: boolean;
}

/**
 * 医師シフトデータテーブルコンポーネント
 * 
 * 意図：
 * - 大量データの効率的な表示
 * - 直感的な操作性の提供
 * - 美しいアニメーションとインタラクション
 * 
 * 注意点：
 * - メモ化による再レンダリング最適化
 * - 仮想スクロールは将来的に実装予定
 * 
 * 改善提案：
 * - react-windowによる仮想スクロール実装
 * - カラムのリサイズ機能
 * - CSVエクスポート機能
 */
export const DoctorShiftTable = memo<DoctorShiftTableProps>(({
  data,
  isLoading,
  onViewDetails,
  onEdit,
  onDelete,
  filters,
  onFiltersChange,
  meta,
  onPageChange,
  onPageSizeChange,
  onSort,
  selectedIds = [],
  onSelectionChange,
  compactMode = false,
}) => {
  const theme = useTheme();
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // 全選択チェックボックスの状態
  const isAllSelected = data.length > 0 && selectedIds.length === data.length;
  const isIndeterminate = selectedIds.length > 0 && selectedIds.length < data.length;

  // 行選択の処理
  const handleRowSelection = (id: string, selected: boolean) => {
    if (!onSelectionChange) return;
    
    if (selected) {
      onSelectionChange([...selectedIds, id]);
    } else {
      onSelectionChange(selectedIds.filter(selectedId => selectedId !== id));
    }
  };

  // 全選択の処理
  const handleSelectAll = (selected: boolean) => {
    if (!onSelectionChange) return;
    
    if (selected) {
      onSelectionChange(data.map(shift => shift.id));
    } else {
      onSelectionChange([]);
    }
  };

  // ページ変更の処理
  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage + 1); // Material-UIは0ベース、APIは1ベース
  };

  // ページサイズ変更の処理
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPageSizeChange(parseInt(event.target.value, 10));
  };

  // ローディング状態のスケルトン行を生成
  const renderSkeletonRows = () => {
    return Array.from({ length: filters.page_size || 10 }).map((_, index) => (
      <TableRow key={index}>
        {onSelectionChange && (
          <TableCell padding="checkbox">
            <Skeleton variant="rounded" width={24} height={24} />
          </TableCell>
        )}
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="circular" width={40} height={40} /></TableCell>
      </TableRow>
    ));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        overflow: 'hidden',
      }}
    >
      <TableContainer sx={{ maxHeight: compactMode ? 400 : 'none' }}>
        <Table stickyHeader={compactMode}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  fontWeight: 600,
                  fontSize: '0.875rem',
                },
              }}
            >
              {onSelectionChange && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    sx={{ color: theme.palette.primary.main }}
                  />
                </TableCell>
              )}
              
              <TableCell>
                <TableSortLabel
                  active={filters.sort_by === 'doctor_id'}
                  direction={filters.sort_by === 'doctor_id' ? filters.sort_order : 'asc'}
                  onClick={() => onSort('doctor_id')}
                >
                  医師ID
                </TableSortLabel>
              </TableCell>
              
              <TableCell>
                医師名
              </TableCell>
              
              <TableCell>
                <TableSortLabel
                  active={filters.sort_by === 'shift_date'}
                  direction={filters.sort_by === 'shift_date' ? filters.sort_order : 'asc'}
                  onClick={() => onSort('shift_date')}
                >
                  シフト日付
                </TableSortLabel>
              </TableCell>
              
              <TableCell>
                <TableSortLabel
                  active={filters.sort_by === 'start_time'}
                  direction={filters.sort_by === 'start_time' ? filters.sort_order : 'asc'}
                  onClick={() => onSort('start_time')}
                >
                  開始時間
                </TableSortLabel>
              </TableCell>
              
              <TableCell>
                <TableSortLabel
                  active={filters.sort_by === 'end_time'}
                  direction={filters.sort_by === 'end_time' ? filters.sort_order : 'asc'}
                  onClick={() => onSort('end_time')}
                >
                  終了時間
                </TableSortLabel>
              </TableCell>
              
              <TableCell>
                <TableSortLabel
                  active={filters.sort_by === 'updated_at'}
                  direction={filters.sort_by === 'updated_at' ? filters.sort_order : 'asc'}
                  onClick={() => onSort('updated_at')}
                >
                  更新日時
                </TableSortLabel>
              </TableCell>
              
              <TableCell align="center" sx={{ width: 120 }}>
                操作
              </TableCell>
            </TableRow>
          </TableHead>
          
          <TableBody>
            {isLoading ? renderSkeletonRows() : (
              data.map((shift, index) => (
                <TableRow
                  key={shift.id}
                  component={motion.tr}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  onMouseEnter={() => setHoveredRow(shift.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  sx={{
                    backgroundColor: hoveredRow === shift.id 
                      ? alpha(theme.palette.primary.main, 0.03) 
                      : selectedIds.includes(shift.id)
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  {onSelectionChange && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(shift.id)}
                        onChange={(e) => handleRowSelection(shift.id, e.target.checked)}
                        sx={{ color: theme.palette.primary.main }}
                      />
                    </TableCell>
                  )}
                  
                  <TableCell>
                    <Chip
                      icon={<DoctorIcon />}
                      label={shift.doctor_id}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {shift.doctor_full_name || shift.doctor_id}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DateIcon color="action" fontSize="small" />
                      <Typography variant="body2">
                        {format(parseISO(shift.shift_date), 'yyyy/MM/dd (E)', { locale: ja })}
                      </Typography>
                    </Stack>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      icon={<TimeIcon />}
                      label={extractTimeHm(shift.start_time)}
                      size="small"
                      sx={{ 
                        minWidth: 80,
                        backgroundColor: '#fce4ec',
                        color: '#e91e63',
                        '& .MuiChip-icon': {
                          color: '#e91e63'
                        }
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      icon={<TimeIcon />}
                      label={extractTimeHm(shift.end_time)}
                      size="small"
                      sx={{ 
                        minWidth: 80,
                        backgroundColor: '#fff3e0',
                        color: '#ff6f00',
                        '& .MuiChip-icon': {
                          color: '#ff6f00'
                        }
                      }}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {format(parseISO(shift.updated_at), 'MM/dd HH:mm', { locale: ja })}
                    </Typography>
                  </TableCell>
                  
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="詳細表示">
                        <IconButton
                          size="small"
                          onClick={() => onViewDetails(shift)}
                          sx={{
                            color: theme.palette.info.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                            },
                          }}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="編集">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(shift)}
                          sx={{
                            color: theme.palette.warning.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.warning.main, 0.1),
                            },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title="削除">
                        <IconButton
                          size="small"
                          onClick={() => onDelete(shift)}
                          sx={{
                            color: theme.palette.error.main,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.error.main, 0.1),
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ページネーション */}
      {meta && (
        <TablePagination
          component="div"
          count={meta.total_records}
          rowsPerPage={meta.page_size}
          page={(meta.current_page || 1) - 1} // APIは1ベース、Material-UIは0ベース
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} / ${count !== -1 ? count : `${to}以上`}`
          }
          sx={{
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            backgroundColor: alpha(theme.palette.background.default, 0.5),
          }}
        />
      )}
    </Paper>
  );
});